
import { ref } from '@vue/composition-api';
import { wrapProperty } from '@nuxtjs/composition-api';
import type Pagination from '~/type/Pagination';
import type { PhotoFilter } from '~/type/PhotoFilter';
import type SortType from '~/type/Sort';
import type { Product } from '~/type/product/Product';
import RowPhotoFilter from '~/components/filter/RowPhotoFilter/RowPhotoFilter.vue';
import FilterRow from '~/components/filter/FilterRow/FilterRow.vue';
import Sort from '~/components/Sort/Sort.vue';
import SortSelect from '~/components/SortSelect/SortSelect.vue';
import SelectFilter from '~/components/filter/SelectFilter/SelectFilter.vue';
import TagHorizontalFilter from '~/components/filter/TagHorizontalFilter/TagHorizontalFilter.vue';
import TSHybridPagination from '~/components/ts-components/TSHybridPagination/TSHybridPagination.vue';
import TSProductCard from '~/components/ts-components/TSProductCard/TSProductCard.vue';
import TSProductCardGrid from '~/components/ts-components/TSProductCardGrid/TSProductCardGrid.vue';
import RowSliderNew from '~/components/sliders/RowSliderNew/RowSliderNew.vue';
import type { BaseFilter } from '~/type/Filter';
type CardsMobileType = 'grid' | 'list';
interface Props {
  loading?: boolean;
  /**
  * Список фильтров в компоненте слайдера
  */
  filterPhotoRow?: PhotoFilter[];
  /**
  * Список фильтров для первой строки фильтров, в мобильной версии первая строка превратится в селект
  */
  filterFirstRow?: BaseFilter[];
  /**
  * Текущее значение для первых фильтров
  */
  filterFirstRowValue?: BaseFilter;
  /**
  * Список фильтров для второй строки фильтров, в мобильной версии вторая строка превратится в горизонтальный список
  */
  filterSecondRow?: BaseFilter[];
  /**
  * Текущее значение для вторых фильтров
  */
  filterSecondRowValue?: BaseFilter;
  /**
  * Название списка
  */
  listName?: string;
  /**
  * Список фильтров, структуру можно увидеть внутри типа Sort
  */
  sortList?: SortType[];
  /**
  * Активный элемент сортировки, структуру можно посмотреть внутри Sort
  */
  activeSort?: SortType;
  productList: Product[];
  quantityOnPage?: number;
  /*
  * Пагинация страницы
  */
  pagination?: Pagination;
  linkPage?: string;
  /**
  *  Стили для адптивности карточек товара
  */
  media?: 'catalog' | 'sale-page' | 'accessory-page';
  filterTag?: 'div' | 'a';
  /**
  * Добавлять ли v-lazy для картинок товара
  */
  productCardPhotoLazyload?: boolean;
  /**
  * Добавлять ли v-lazy для картинок фильтров
  */
  rowPhotoFilterPhotoLazyload?: boolean;
  /**
  * Количество картинок, которые буду загружаться без js, если активен пропс productCardPhotoLazyload
  */
  countProductCardPhotoNoLazyload?: number;
  /**
  * Чекбокс для сортировки с учетом наличия в десктопе
  */
  availability?: boolean;
  /**
  * Начальное значение для фильтра-селекта в мобильной версии, который стоит справа от сортировки
  */
  selectFilterPlaceholder?: string;
  hideSort?: boolean;
  hideFilterRow?: boolean;
  /**
   * Показывать ли:
   * mobile-border - границы в мобильной версии
   * mobile-more - кнопка с тремя точками в мобильной версии
   * desktop-tools - user-tools (кнопки избранного и сравнения) в десктоп версии
   * bonus - бонусы
   * price - цена
   * mobile-favorite - кнопка избранного в мобильной версии
   * product-card-slider - слайдер изображений
   */
  hide?: ('mobile-border' | 'mobile-more' | 'desktop-tools' | 'bonus' | 'price' | 'mobile-favorite' | 'product-card-slider')[];
  /* отображать кнопку смены вида грида во втором ряду на мобилке */
  isMobileGridTypeBtnOnSecondRow: boolean;
}
const __sfc_main = {};
__sfc_main.props = {
  loading: {
    key: "loading",
    required: false,
    type: Boolean,
    default: false
  },
  filterPhotoRow: {
    key: "filterPhotoRow",
    required: false,
    type: Array,
    default: () => []
  },
  filterFirstRow: {
    key: "filterFirstRow",
    required: false,
    type: Array,
    default: () => []
  },
  filterFirstRowValue: {
    key: "filterFirstRowValue",
    required: false,
    type: null
  },
  filterSecondRow: {
    key: "filterSecondRow",
    required: false,
    type: Array,
    default: () => []
  },
  filterSecondRowValue: {
    key: "filterSecondRowValue",
    required: false,
    type: null
  },
  listName: {
    key: "listName",
    required: false,
    type: String,
    default: ''
  },
  sortList: {
    key: "sortList",
    required: false,
    type: Array,
    default: () => []
  },
  activeSort: {
    key: "activeSort",
    required: false,
    type: null,
    default: () => ({
      active: false,
      name: '',
      value: ''
    })
  },
  productList: {
    key: "productList",
    required: true,
    type: Array
  },
  quantityOnPage: {
    key: "quantityOnPage",
    required: false,
    type: Number,
    default: 0
  },
  pagination: {
    key: "pagination",
    required: false,
    type: null,
    default: () => ({
      count: 0,
      current: 0,
      itemQuantity: 0
    })
  },
  linkPage: {
    key: "linkPage",
    required: false,
    type: String,
    default: ''
  },
  media: {
    key: "media",
    required: false,
    type: String,
    default: 'catalog'
  },
  filterTag: {
    key: "filterTag",
    required: false,
    type: String,
    default: 'div'
  },
  productCardPhotoLazyload: {
    key: "productCardPhotoLazyload",
    required: false,
    type: Boolean,
    default: true
  },
  rowPhotoFilterPhotoLazyload: {
    key: "rowPhotoFilterPhotoLazyload",
    required: false,
    type: Boolean,
    default: true
  },
  countProductCardPhotoNoLazyload: {
    key: "countProductCardPhotoNoLazyload",
    required: false,
    type: Number,
    default: 0
  },
  availability: {
    key: "availability",
    required: false,
    type: Boolean,
    default: false
  },
  selectFilterPlaceholder: {
    key: "selectFilterPlaceholder",
    required: false,
    type: String,
    default: 'Производитель'
  },
  hideSort: {
    key: "hideSort",
    required: false,
    type: Boolean,
    default: false
  },
  hideFilterRow: {
    key: "hideFilterRow",
    required: false,
    type: Boolean,
    default: false
  },
  hide: {
    key: "hide",
    required: false,
    type: Array
  },
  isMobileGridTypeBtnOnSecondRow: {
    key: "isMobileGridTypeBtnOnSecondRow",
    required: false,
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const emit = __ctx.emit;
  const cookies = wrapProperty('$cookies', false)();
  const cardsMobileType = ref<CardsMobileType>('grid');

  /*
    * Сохраняет вид карточек в cookies
    */
  if (cookies.get('cardsMobileType')) cardsMobileType.value = (cookies.get('cardsMobileType') as CardsMobileType);
  function changeCardsMobileType() {
    /**
      * Меняет вид мобильных карточек с плитки на подробный и наоборот
      */
    cardsMobileType.value === 'grid' ? cardsMobileType.value = 'list' : cardsMobileType.value = 'grid';
    cookies.set('cardsMobileType', cardsMobileType.value);
  }
  function onSort(e: SortType) {
    /**
      * Срабатывает при нажатии на сортировку
      */
    emit('sort', e);
  }
  function onFilterFirstRowChange(e?: BaseFilter) {
    /**
      * Срабатывает при изменении фильтров первой строки
      */
    emit('filter-first-row-change', e);
  }
  return {
    emit,
    cardsMobileType,
    changeCardsMobileType,
    onSort,
    onFilterFirstRowChange
  };
};
__sfc_main.components = Object.assign({
  RowSliderNew,
  RowPhotoFilter,
  FilterRow,
  Sort,
  SortSelect,
  SelectFilter,
  TagHorizontalFilter,
  TSProductCardGrid,
  TSProductCard,
  TSHybridPagination
}, __sfc_main.components);
export default __sfc_main;
